import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { User, IdCard, Phone, Mail, Calendar, MapPin, Clock, MoreVertical, Pencil, Trash2, ScanLine } from 'lucide-react';
import { Driver } from '@/hooks/useDrivers';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { UploadDriverLicenseDialog } from './UploadDriverLicenseDialog';

interface DriverCardProps {
  driver: Driver;
  onEdit: (driver: Driver) => void;
  onDelete: (driverId: string) => void;
}

export const DriverCard = ({ driver, onEdit, onDelete }: DriverCardProps) => {
  const { t, i18n } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  console.log('DriverCard - driver data:', {
    id: driver.id,
    users: driver.users,
    first_name: driver.users?.first_name,
    last_name: driver.users?.last_name
  });

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };

  const getStatusBadge = () => {
    switch (driver.status) {
      case 'available':
        return (
          <Badge className="bg-[#14A38B]/10 text-[#14A38B] border-[#14A38B]/20 font-['Funnel_Sans']">
            {t('fleet.driverDetails.available')}
          </Badge>
        );
      case 'on_mission':
        return (
          <Badge className="bg-[#0880AE]/10 text-[#0880AE] border-[#0880AE]/20 font-['Funnel_Sans']">
            {t('fleet.driverDetails.onMission')}
          </Badge>
        );
      case 'off_duty':
        return (
          <Badge className="bg-[#5A6B7A]/10 text-[#5A6B7A] border-[#5A6B7A]/20 font-['Funnel_Sans']">
            {t('fleet.driverDetails.offDuty')}
          </Badge>
        );
      default:
        return null;
    }
  };

  const firstName = driver.users?.first_name || '';
  const lastName = driver.users?.last_name || '';
  const driverName = firstName && lastName 
    ? `${firstName} ${lastName}`.trim()
    : firstName || lastName || t('fleet.driverDetails.noName');

  return (
    <>
      <Card className="border border-[#DBE2EA] rounded-xl hover:shadow-md hover:border-[#0880AE] transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0880AE]/10 rounded-full flex items-center justify-center">
                <User className="text-[#0880AE]" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#37699E] font-['Lexend_Deca']">
                  {driverName}
                </h3>
                <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans'] flex items-center gap-1">
                  <IdCard size={12} />
                  {driver.license_number}
                </p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-[#5A6B7A] hover:bg-[#EBF4F8]"
                >
                  <MoreVertical size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white z-50">
                <DropdownMenuItem
                  onClick={() => setShowLicenseDialog(true)}
                  className="cursor-pointer font-['Funnel_Sans']"
                >
                  <ScanLine size={16} className="mr-2 text-[#0880AE]" />
                  {driver.license_verified_at
                    ? t('fleet.documentStatus.rescan')
                    : t('fleet.documentStatus.scanLicense')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onEdit(driver)}
                  className="cursor-pointer font-['Funnel_Sans']"
                >
                  <Pencil size={16} className="mr-2 text-[#0880AE]" />
                  {t('fleet.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="cursor-pointer text-[#FF7171] focus:text-[#FF7171] font-['Funnel_Sans']"
                >
                  <Trash2 size={16} className="mr-2" />
                  {t('fleet.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {getStatusBadge()}
            <DocumentStatusBadge
              verifiedAt={driver.license_verified_at}
              expiry={driver.license_expiry}
              label={t('fleet.documentStatus.driverLicense')}
            />
          </div>
          {driver.users?.email && (
            <div className="flex items-center gap-2 text-sm text-[#2D3748] font-['Funnel_Sans']">
              <Mail className="text-[#5A6B7A]" size={14} />
              <span className="text-[#5A6B7A]">{driver.users.email}</span>
            </div>
          )}
          {driver.users?.phone && (
            <div className="flex items-center gap-2 text-sm text-[#2D3748] font-['Funnel_Sans']">
              <Phone className="text-[#5A6B7A]" size={14} />
              <span className="text-[#5A6B7A]">{driver.users.phone}</span>
            </div>
          )}
          
          <div className="pt-2 border-t border-[#DBE2EA] space-y-2">
            <div className="flex items-center gap-2 text-sm text-[#2D3748] font-['Funnel_Sans']">
              <Calendar className="text-[#0880AE]" size={14} />
              <span className="text-[#5A6B7A]">
                {t('fleet.driverDetails.licenseExpiresOn')} {format(new Date(driver.license_expiry), 'dd MMM yyyy', { locale: getDateLocale() })}
              </span>
            </div>
            
            {(driver.current_location_lat && driver.current_location_lng) ? (
              <div className="flex items-center gap-2 text-sm text-[#2D3748] font-['Funnel_Sans']">
                <MapPin className="text-[#14A38B]" size={14} />
                <span className="text-[#5A6B7A]">
                  {t('fleet.driverDetails.position')}: {driver.current_location_lat.toFixed(4)}, {driver.current_location_lng.toFixed(4)}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-[#2D3748] font-['Funnel_Sans']">
                <MapPin className="text-[#5A6B7A]" size={14} />
                <span className="text-[#5A6B7A]">{t('fleet.driverDetails.positionUnavailable')}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-sm text-[#2D3748] font-['Funnel_Sans']">
              <Clock className="text-[#5A6B7A]" size={14} />
              <span className="text-[#5A6B7A]">
                {t('fleet.driverDetails.addedOn')} {format(new Date(driver.created_at), 'dd MMM yyyy', { locale: getDateLocale() })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#37699E] font-['Lexend_Deca']">
              {t('fleet.deleteDialog.driverTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-['Funnel_Sans']">
              {t('fleet.deleteDialog.driverDescription')} <strong>{driverName}</strong> ?
              {' '}{t('fleet.deleteDialog.irreversible')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#DBE2EA] font-['Funnel_Sans']">
              {t('fleet.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(driver.id);
                setShowDeleteDialog(false);
              }}
              className="bg-[#FF7171] hover:bg-[#E65959] text-white font-['Funnel_Sans']"
            >
              {t('fleet.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UploadDriverLicenseDialog
        open={showLicenseDialog}
        onOpenChange={setShowLicenseDialog}
        driverId={driver.id}
        driverName={driverName}
      />
    </>
  );
};